import { useContext, useMemo, useState } from "react";
import { AppContext } from "../Context/MainContext";
import TextFilterComponent from "./Filtering/TextFilter.component";
import Layout from "../Layout/Layout.component";
import DataTable from "react-data-table-component";
import SelectFilter from "./Filtering/SelectFilter.component";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import ClientAxios from "../Client.axios";
import PaymentDocFormComponent from "./Forms/PaymentDocForm.component";

const MySwal = withReactContent(Swal);

interface PaymentDocTableProps {
    title: string;
    data: any[];
    columns: any[];
}

const PaymentDocTableComponent = ({ title, data, columns }: PaymentDocTableProps) => {

    const { transactions, setTransactions, selectedProject, controlSheets } = useContext(AppContext);
    const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
    const [filterText, setFilterText] = useState('');
    const [filterFamily, setFilterFamily] = useState('');

    const filteredItems = data
        .filter(item => {
            if (!filterText) return true;

            // Create an object with just the fields shown in the table
            const itemToSearch: any = {};
            columns.forEach((col: any, index: number) => {
                const isMonto = col.name === "Monto" || (col.name?.props?.children === "Monto");
                if (!isMonto && col.selector && typeof col.selector === 'function') {
                    try {
                        itemToSearch[`col_${index}`] = col.selector(item);
                    } catch (error) {
                        // ignore selector errors
                    }
                }
            });

            const searchString = JSON.stringify(itemToSearch).toLowerCase();
            return searchString.includes(filterText.toLowerCase());
        })
        .filter(item => {
            if (filterFamily === "Sin Familia") {
                return !item.family;
            } else if (filterFamily === "") {
                return true;
            } else {
                return item.family && item.family.toLowerCase().includes(filterFamily.toLowerCase());
            }
        });

    const subHeaderComponentMemo = useMemo(() => {
        const handleClear = () => {
            if (filterText) {
                setResetPaginationToggle(!resetPaginationToggle);
                setFilterText('');
            }
        };
        const handleClearFamily = () => {
            if (filterFamily) {
                setResetPaginationToggle(!resetPaginationToggle);
                setFilterFamily('');
            }
        }
        return <>
            <SelectFilter
                selectOptions={["Sin Familia", ...selectedProject.families.filter((f: any) => f.selectable).map((f: any) => f.name)]}
                selected={filterFamily}
                onFilter={value => setFilterFamily(value)}
                onClear={handleClearFamily}
            />

            <TextFilterComponent onFilter={value => setFilterText(value)} onClear={handleClear} filterText={filterText} />
        </>;
    }, [filterText, filterFamily, resetPaginationToggle, selectedProject]);

    const showInvoiceEditModal = (row: any) => {
        MySwal.fire({
            title: `Editar ${title}`,
            html: (<PaymentDocFormComponent
                project={selectedProject}
                controlSheets={controlSheets}
                doc={row}
                handleSubmit={(data: any) => {
                    let payload = { ...row, ...data };

                    // Update the transaction
                    let client = ClientAxios.getInstance();
                    client.updateTransaction(payload)
                        .then((response: any) => {
                            if (response.error) {
                                MySwal.fire("Error", response.error, "error");
                            } else {
                                let newTransaction = response.data;
                                let transactionsTmp = transactions.map((tr: any) => {
                                    if (tr._id === newTransaction._id) {
                                        return newTransaction;
                                    }
                                    return tr;
                                });
                                setTransactions(transactionsTmp);
                                MySwal.fire("Guardado", "El documento ha sido actualizado", "success");
                            }
                        });
                    MySwal.close();
                }}
            />),
            showCancelButton: true,
            showConfirmButton: false,
            cancelButtonText: "Cancelar",
        }).then((result: any) => {
            if (result.isConfirmed) {
                MySwal.fire("Guardado", "El documento ha sido actualizado", "success");
            }
        });
    }

    const tableColumns = [
        ...columns,
        {
            wrap: true, maxWidth: "10%", name: "Acciones", cell: (row: any) => {
                return <div className="flex gap-2">
                    <button className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                        onClick={() => showInvoiceEditModal(row)}>Editar</button>
                </div>
            }
        }
    ];

    const paginationComponentOptions = {
        rowsPerPageText: 'Filas por página',
        rangeSeparatorText: 'de',
        selectAllRowsItem: true,
        selectAllRowsItemText: 'Todos',
    };

    return (<Layout>
        <div className="container">
            <h1 className="text-2xl font-bold mb-4">{title}</h1>
            <DataTable
                columns={tableColumns}
                data={filteredItems}
                pagination={true}
                paginationPerPage={50}
                paginationRowsPerPageOptions={[50, 100, 200, filteredItems.length > 0 ? filteredItems.length : 9999]}
                paginationComponentOptions={paginationComponentOptions}
                defaultSortFieldId={1}
                defaultSortAsc={false}
                subHeader
                subHeaderComponent={subHeaderComponentMemo}
                persistTableHead
            />
        </div>
    </Layout>

    );
}

export default PaymentDocTableComponent;
